import { Link } from 'react-router-dom'
import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SECTION_LABELS, type HelpPage } from './registry'
import { SNIPPETS_PER_PAGE, type SearchOutcome, type SnippetPart } from './search'

/** The sections a search can be narrowed to, in sidebar order (AC5). */
const SECTIONS = ['user-manual', 'dev-docs', 'releases', ''] as const

/** Where a page lives: the home page is `/wiki`, every other page `/wiki/<section>/<slug>`. */
export function wikiPath(page: Pick<HelpPage, 'urlPath'>): string {
  return page.urlPath === 'index' ? '/wiki' : `/wiki/${page.urlPath}`
}

/**
 * Wiki search results (z8nrz7dyd3): each result names its section and its
 * source document, and shows the passages that matched with the words
 * highlighted. The outcome is already permission-filtered; this only renders.
 */
export function WikiSearchResults({
  query,
  outcome,
  section,
  onSection,
}: {
  query: string
  outcome: SearchOutcome
  /** The section the results are narrowed to; undefined for all. */
  section: string | undefined
  onSection: (section: string | undefined) => void
}) {
  const total = Object.values(outcome.bySection).reduce((n, c) => n + c, 0)
  const { results } = outcome

  return (
    <div className='mx-auto w-full max-w-3xl px-8 py-10'>
      <h1 className='text-xl font-semibold tracking-tight'>Search results</h1>
      <p className='mt-1 text-sm text-muted-foreground'>
        {total === 0
          ? 'Every word must appear on a page; put a phrase in quotes to match it exactly.'
          : `${total} page${total === 1 ? '' : 's'} match “${query.trim()}”.`}
      </p>

      <div className='mt-4 flex flex-wrap gap-1.5' role='group' aria-label='Narrow to a section'>
        <SectionChip label='All' count={total} active={section === undefined} onClick={() => onSection(undefined)} />
        {SECTIONS.map(s => (
          <SectionChip
            key={s || 'home'}
            label={SECTION_LABELS[s] ?? s}
            count={outcome.bySection[s] ?? 0}
            active={section === s}
            onClick={() => onSection(s)}
          />
        ))}
      </div>

      {results.length === 0 ? (
        <div className='mt-8 rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground'>
          {total === 0 ? (
            <>No pages match “{query.trim()}”.</>
          ) : (
            <>
              No {SECTION_LABELS[section ?? ''] ?? 'matching'} pages match “{query.trim()}”.{' '}
              <button type='button' className='underline underline-offset-2' onClick={() => onSection(undefined)}>
                Show all {total}
              </button>
            </>
          )}
        </div>
      ) : (
        <ol className='mt-6 space-y-4'>
          {results.map(r => (
            <li key={r.page.urlPath} className='rounded-lg border p-4'>
              <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                <Link to={wikiPath(r.page)} className='text-base font-medium text-foreground hover:underline'>
                  <Highlighted parts={r.title} />
                </Link>
                <span className='rounded-full border bg-muted/50 px-2 py-0.5 text-[11px] font-medium text-muted-foreground'>
                  {SECTION_LABELS[r.page.section] ?? r.page.section}
                </span>
              </div>
              <SourceLine page={r.page} />
              {r.snippets.length > 0 ? (
                <ul className='mt-2 space-y-1.5'>
                  {r.snippets.map((parts, i) => (
                    <li key={i} className='text-sm leading-relaxed text-muted-foreground'>
                      <Highlighted parts={parts} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='mt-2 text-sm text-muted-foreground'>Matched in the page title.</p>
              )}
              {r.bodyMatches > SNIPPETS_PER_PAGE && r.snippets.length === SNIPPETS_PER_PAGE && (
                <p className='mt-1.5 text-xs text-muted-foreground'>
                  Showing {SNIPPETS_PER_PAGE} passages of {r.bodyMatches} matches —{' '}
                  <Link to={wikiPath(r.page)} className='underline underline-offset-2'>
                    open the page
                  </Link>{' '}
                  for the rest.
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/**
 * The page's upstream source as it records it (AC6): title, identifier and
 * version or effective date. A page that records none says so (AC7) — never
 * blank, never guessed.
 */
export function SourceLine({ page, className }: { page: Pick<HelpPage, 'source'>; className?: string }) {
  return (
    <p className={cn('mt-1 flex items-center gap-1.5 text-xs text-muted-foreground', className)}>
      <FileText className='h-3 w-3 shrink-0' />
      {page.source ? (
        <span>
          Source: <span className='font-medium text-foreground'>{page.source.title}</span> · {page.source.id} ·{' '}
          {page.source.version}
        </span>
      ) : (
        <span className='italic'>Source not recorded</span>
      )}
    </p>
  )
}

function Highlighted({ parts }: { parts: SnippetPart[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <mark key={i} className='rounded-sm bg-yellow-200/80 px-0.5 text-foreground dark:bg-yellow-500/30'>
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  )
}

function SectionChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {label} <span className='tabular-nums opacity-70'>{count}</span>
    </button>
  )
}
